import React, { useState, useEffect } from "react";
import { Modal, Descriptions, Tag, Card, Tabs, Space, Button, Timeline, Typography, Divider, message, Row, Col, Alert, Form } from "antd";
import { ClockCircleOutlined, UserOutlined, EyeOutlined, CodeOutlined, HistoryOutlined, FullscreenOutlined, FullscreenExitOutlined, MailOutlined, TeamOutlined, ContactsOutlined, EditOutlined, SaveOutlined, GlobalOutlined, ShopOutlined } from "@ant-design/icons";
import { emailTemplateService } from "../../../api/enhancedApi";
import { processHtmlWithSampleData } from "./utils/contentBlockUtils";
import RecipientSelector from "../../../components/EmailEditor/components/RecipientSelector";
import AirportSelector from "../../../components/common/AirportSelector";
import SupplierSelector from "../../../components/common/SupplierSelector";
import "./EmailTemplateDetail.css";

// const { TabPane } = Tabs; // 已废弃，使用items属性
const { Text, Paragraph } = Typography;

// 收件人配置显示组件
const RecipientConfigDisplay = ({ config, title, icon }) => {
    if (!config || (!config.addresses?.length && !config.groups?.length)) {
        return (
            <div style={{ color: "#999", fontStyle: "italic" }}>
                {icon} 未配置{title}
            </div>
        );
    }

    return (
        <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>
                {icon} {title}
            </div>

            {config.addresses?.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                        邮箱地址：
                    </Text>
                    <div className="recipient-tag-group">
                        {config.addresses.map((address, index) => (
                            <Tag key={index} color="default" icon={<MailOutlined />}>
                                {address}
                            </Tag>
                        ))}
                    </div>
                </div>
            )}

            {config.groups?.length > 0 && (
                <div>
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                        邮箱分组：
                    </Text>
                    <div className="recipient-tag-group">
                        {config.groups.map((group, index) => (
                            <Tag key={index} color="blue" icon={<TeamOutlined />}>
                                {group}
                            </Tag>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// 范围编辑组件
const ScopeEditor = ({ templateData, onSave, loading }) => {
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);

    // 初始化表单值
    useEffect(() => {
        if (templateData) {
            form.setFieldsValue({
                applicable_airports: templateData.applicable_airports || [],
                applicable_suppliers: templateData.applicable_suppliers || [],
            });
        }
    }, [templateData, form]);

    const handleSave = async () => {
        try {
            setSaving(true);
            const values = await form.validateFields();

            const updateData = {
                applicable_airports: values.applicable_airports?.length > 0 ? values.applicable_airports : null,
                applicable_suppliers: values.applicable_suppliers?.length > 0 ? values.applicable_suppliers : null,
            };

            await onSave(updateData);
            message.success("适用范围保存成功");
        } catch (error) {
            if (error.errorFields) {
                message.error("请检查表单输入");
            } else {
                message.error("保存失败，请重试");
                console.error("Save scope config error:", error);
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
                <Text type="secondary">正在加载模板数据...</Text>
            </div>
        );
    }

    return (
        <div className="scope-editor-container">
            <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Text strong style={{ fontSize: "16px" }}>
                    适用范围配置
                </Text>
                <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
                    保存配置
                </Button>
            </div>

            <Alert
                message="适用范围配置说明"
                description="配置此模板适用的机场和供应商范围。留空表示适用于所有机场/供应商。在邮件发送时，系统会根据当前选择的机场和供应商智能筛选出适用的模板。"
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
            />

            <Form form={form} layout="vertical">
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label={
                                <Space>
                                    <GlobalOutlined style={{ color: "#1890ff" }} />
                                    <span>适配机场</span>
                                </Space>
                            }
                            name="applicable_airports"
                            extra="选择此模板适用的机场，留空表示适用于所有机场"
                        >
                            <AirportSelector
                                mode="multiple"
                                valueType="iata_code"
                                placeholder="选择适配的机场"
                                allowClear
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label={
                                <Space>
                                    <ShopOutlined style={{ color: "#52c41a" }} />
                                    <span>适配供应商</span>
                                </Space>
                            }
                            name="applicable_suppliers"
                            extra="选择此模板适用的供应商，留空表示适用于所有供应商"
                        >
                            <SupplierSelector
                                mode="multiple"
                                placeholder="选择适配的供应商"
                                allowClear
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

// 收件人编辑组件
const RecipientEditor = ({ templateData, onSave, loading }) => {
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);

    // 将配置对象转换为数组格式（用于 RecipientSelector）
    const configToArray = (config) => {
        if (!config) return [];
        const result = [];
        if (config.addresses) result.push(...config.addresses);
        if (config.groups) result.push(...config.groups);
        return result;
    };

    // 将数组转换为配置对象格式
    const arrayToConfig = (array) => {
        const addresses = [];
        const groups = [];

        if (array && array.length > 0) {
            array.forEach((item) => {
                if (typeof item === "string" && item.trim() && item.includes("@")) {
                    // 邮箱地址：包含 @ 符号
                    addresses.push(item.trim());
                } else if (typeof item === "string" && item.trim()) {
                    // 邮箱分组：不包含 @ 符号
                    groups.push(item.trim());
                }
            });
        }

        // 根据后端 RecipientConfig 模型，如果有任何配置就返回对象，否则返回 null
        if (addresses.length > 0 || groups.length > 0) {
            return {
                addresses: addresses,
                groups: groups,
            };
        } else {
            return null;
        }
    };

    // 初始化表单值
    useEffect(() => {
        if (templateData) {
            form.setFieldsValue({
                default_to: configToArray(templateData.default_to),
                default_cc: configToArray(templateData.default_cc),
                // 暂时不处理 BCC，因为表单中已注释掉
                // default_bcc: configToArray(templateData.default_bcc),
            });
        }
    }, [templateData, form]);

    const handleSave = async () => {
        try {
            setSaving(true);
            const values = await form.validateFields();

            // 转换数据格式
            const updateData = {
                default_to: arrayToConfig(values.default_to),
                default_cc: arrayToConfig(values.default_cc),
                // 暂时不处理 BCC，因为表单中已注释掉
                // default_bcc: arrayToConfig(values.default_bcc),
            };

            await onSave(updateData);
            message.success("收件人配置保存成功");
        } catch (error) {
            if (error.errorFields) {
                message.error("请检查表单输入");
            } else {
                message.error("保存失败，请重试");
                console.error("Save recipient config error:", error);
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
                <Text type="secondary">正在加载模板数据...</Text>
            </div>
        );
    }

    return (
        <div className="recipient-editor-container">
            <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Text strong style={{ fontSize: "16px" }}>
                    收件人配置
                </Text>
                <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
                    保存配置
                </Button>
            </div>

            <Alert 
                message="收件人配置说明" 
                description={
                    templateData?.is_system 
                        ? "您正在编辑系统内置模板的收件人配置。您可以为模板配置默认的收件人、抄送人和密送人。支持选择邮箱分组、已存在的邮箱地址，或手动输入新的邮箱地址。保存后，使用此模板发送邮件时将自动填充这些收件人信息。"
                        : "您可以为模板配置默认的收件人、抄送人和密送人。支持选择邮箱分组、已存在的邮箱地址，或手动输入新的邮箱地址。保存后，使用此模板发送邮件时将自动填充这些收件人信息。"
                } 
                type={templateData?.is_system ? "warning" : "info"} 
                showIcon 
                style={{ marginBottom: 24 }} 
            />

            <Form form={form} layout="vertical">
                <Form.Item
                    label={
                        <Space>
                            <ContactsOutlined style={{ color: "#1890ff" }} />
                            <span>默认收件人 (To)</span>
                        </Space>
                    }
                    name="default_to"
                >
                    <RecipientSelector placeholder="选择或输入收件人邮箱地址" allowGroups={true} allowAddresses={true} allowCustomInput={true} style={{ width: "100%" }} />
                </Form.Item>

                <Form.Item
                    label={
                        <Space>
                            <ContactsOutlined style={{ color: "#52c41a" }} />
                            <span>默认抄送人 (CC)</span>
                        </Space>
                    }
                    name="default_cc"
                >
                    <RecipientSelector placeholder="选择或输入抄送人邮箱地址" allowGroups={true} allowAddresses={true} allowCustomInput={true} style={{ width: "100%" }} />
                </Form.Item>

                {/* 暂时不用 */}
                {/* <Form.Item
                    label={
                        <Space>
                            <ContactsOutlined style={{ color: '#faad14' }} />
                            <span>默认密送人 (BCC)</span>
                        </Space>
                    }
                    name="default_bcc"
                >
                    <RecipientSelector
                        placeholder="选择或输入密送人邮箱地址"
                        allowGroups={true}
                        allowAddresses={true}
                        allowCustomInput={true}
                        style={{ width: '100%' }}
                    />
                </Form.Item> */}
            </Form>
        </div>
    );
};

// 邮件预览组件
const EmailPreview = ({ subject, bodyHtml, loading, templateData }) => {
    const [fullscreen, setFullscreen] = useState(false);

    const toggleFullscreen = () => {
        setFullscreen(!fullscreen);
    };

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
                <Text type="secondary">正在加载模板内容...</Text>
            </div>
        );
    }

    // 处理HTML内容，添加示例数据
    const processedHtml = processHtmlWithSampleData(bodyHtml);

    const previewContent = (
        <div className="email-preview-container">
            {/* 邮件头部信息 */}
            <div className="email-preview-header">
                <Row gutter={16} style={{ marginBottom: 8 }}>
                    <Col span={3}>
                        <Text strong>主题：</Text>
                    </Col>
                    <Col span={21}>
                        <Text>{subject || "无主题"}</Text>
                    </Col>
                </Row>
                <Row gutter={16} style={{ marginBottom: 8 }}>
                    <Col span={3}>
                        <Text strong>发件人：</Text>
                    </Col>
                    <Col span={21}>
                        <Text type="secondary">system@example.com (示例)</Text>
                    </Col>
                </Row>
                <Row gutter={16} style={{ marginBottom: 8 }}>
                    <Col span={3}>
                        <Text strong>收件人：</Text>
                    </Col>
                    <Col span={21}>
                        {templateData?.default_to ? (
                            <Space wrap>
                                {templateData.default_to.addresses?.map((addr, idx) => (
                                    <Tag key={idx} color="default" icon={<MailOutlined />}>
                                        {addr}
                                    </Tag>
                                ))}
                                {templateData.default_to.groups?.map((group, idx) => (
                                    <Tag key={idx} color="blue" icon={<TeamOutlined />}>
                                        {group}
                                    </Tag>
                                ))}
                            </Space>
                        ) : (
                            <Text type="secondary">recipient@example.com (示例)</Text>
                        )}
                    </Col>
                </Row>

                {/* 抄送人 */}
                {templateData?.default_cc && (templateData.default_cc.addresses?.length > 0 || templateData.default_cc.groups?.length > 0) && (
                    <Row gutter={16} style={{ marginBottom: 8 }}>
                        <Col span={3}>
                            <Text strong>抄送：</Text>
                        </Col>
                        <Col span={21}>
                            <Space wrap>
                                {templateData.default_cc.addresses?.map((addr, idx) => (
                                    <Tag key={idx} color="default" icon={<MailOutlined />}>
                                        {addr}
                                    </Tag>
                                ))}
                                {templateData.default_cc.groups?.map((group, idx) => (
                                    <Tag key={idx} color="blue" icon={<TeamOutlined />}>
                                        {group}
                                    </Tag>
                                ))}
                            </Space>
                        </Col>
                    </Row>
                )}

                {/* 密送人 */}
                {templateData?.default_bcc && (templateData.default_bcc.addresses?.length > 0 || templateData.default_bcc.groups?.length > 0) && (
                    <Row gutter={16}>
                        <Col span={3}>
                            <Text strong>密送：</Text>
                        </Col>
                        <Col span={21}>
                            <Space wrap>
                                {templateData.default_bcc.addresses?.map((addr, idx) => (
                                    <Tag key={idx} color="default" icon={<MailOutlined />}>
                                        {addr}
                                    </Tag>
                                ))}
                                {templateData.default_bcc.groups?.map((group, idx) => (
                                    <Tag key={idx} color="blue" icon={<TeamOutlined />}>
                                        {group}
                                    </Tag>
                                ))}
                            </Space>
                        </Col>
                    </Row>
                )}
            </div>

            <Divider style={{ margin: "16px 0" }} />

            {/* 邮件正文预览 */}
            <div className="email-preview-body">
                {processedHtml ? (
                    <div
                        className="email-content-preview"
                        dangerouslySetInnerHTML={{ __html: processedHtml }}
                        style={{
                            border: "1px solid #f0f0f0",
                            borderRadius: "4px",
                            padding: "20px",
                            backgroundColor: "#fff",
                            fontFamily: "Arial, sans-serif",
                            lineHeight: "1.6",
                            color: "#333",
                        }}
                    />
                ) : (
                    <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>
                        <Text type="secondary">暂无邮件内容可预览</Text>
                    </div>
                )}
            </div>
        </div>
    );

    if (fullscreen) {
        return (
            <Modal
                title={
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span>邮件预览 - 全屏模式</span>
                        <Button type="text" icon={<FullscreenExitOutlined />} onClick={toggleFullscreen} />
                    </div>
                }
                open={fullscreen}
                onCancel={toggleFullscreen}
                width="100%"
                footer={null}
                styles={{
                    body: { padding: "20px", maxHeight: "calc(100vh - 120px)", overflow: "auto" },
                    content: { height: "100vh", maxHeight: "100vh" },
                }}
                className="email-preview-fullscreen"
            >
                {previewContent}
            </Modal>
        );
    }

    return (
        <div style={{ position: "relative" }}>
            <div style={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
                <Button type="text" size="small" icon={<FullscreenOutlined />} onClick={toggleFullscreen} title="全屏预览" />
            </div>
            {previewContent}
        </div>
    );
};

const EmailTemplateDetail = ({ visible, template, onCancel }) => {
    const [versions, setVersions] = useState([]);
    const [loadingVersions, setLoadingVersions] = useState(false);
    const [fullTemplate, setFullTemplate] = useState(null);
    const [loadingTemplate, setLoadingTemplate] = useState(false);

    useEffect(() => {
        if (visible && template) {
            loadTemplateDetail();
            loadVersions();
        }
    }, [visible, template]);

    const loadTemplateDetail = async () => {
        if (!template) return;

        try {
            setLoadingTemplate(true);
            const response = await emailTemplateService.getTemplateById(template.id);
            setFullTemplate(response.data);
        } catch (error) {
            message.error("加载模板详情失败");
            console.error("Load template detail error:", error);
        } finally {
            setLoadingTemplate(false);
        }
    };

    const loadVersions = async () => {
        if (!template) return;

        try {
            setLoadingVersions(true);
            const response = await emailTemplateService.getTemplateVersions(template.id);
            setVersions(response.data);
        } catch (error) {
            message.error("加载版本历史失败");
            console.error("Load versions error:", error);
        } finally {
            setLoadingVersions(false);
        }
    };

    // 保存收件人配置
    const handleSaveRecipientConfig = async (newConfig) => {
        if (!template) return;

        try {
            const response = await emailTemplateService.updateTemplate(template.id, newConfig, "更新收件人配置");

            // 更新本地模板数据
            setFullTemplate((prev) => ({
                ...prev,
                ...newConfig,
                updated_at: new Date().toISOString(),
            }));

            return response;
        } catch (error) {
            console.error("Save recipient config error:", error);
            throw error;
        }
    };

    // 保存范围配置
    const handleSaveScopeConfig = async (newConfig) => {
        if (!template) return;

        try {
            const response = await emailTemplateService.updateTemplate(template.id, newConfig, "更新适用范围配置");

            // 更新本地模板数据
            setFullTemplate((prev) => ({
                ...prev,
                ...newConfig,
                updated_at: new Date().toISOString(),
            }));

            return response;
        } catch (error) {
            console.error("Save scope config error:", error);
            throw error;
        }
    };

    if (!template) return null;

    // 使用完整的模板数据，如果还在加载则使用基本模板数据
    const displayTemplate = fullTemplate || template;

    const getStatusTag = (status) => {
        const statusConfig = {
            ACTIVE: { color: "green", text: "启用" },
            INACTIVE: { color: "red", text: "禁用" },
            DRAFT: { color: "orange", text: "草稿" },
        };
        const config = statusConfig[status] || { color: "default", text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    const getCategoryText = (category) => {
        const categoryMap = {
            FLIGHT_OPERATION: "航班运行",
            FUEL_REQUEST: "加油申请",
            CATERING_ORDER: "餐食订单",
            CREW_TRANSPORTATION: "机组交通",
            HOTAC: "酒店住宿",
            PERMIT_REQUEST: "许可申请",
            GENERAL: "通用",
        };
        return categoryMap[category] || category;
    };

    const formatDateTime = (dateTime) => {
        return new Date(dateTime).toLocaleString("zh-CN");
    };

    return (
        <Modal
            title={`模板详情 - ${displayTemplate.name}`}
            open={visible}
            onCancel={onCancel}
            width={1200}
            footer={[
                <Button key="close" onClick={onCancel}>
                    关闭
                </Button>,
            ]}
            destroyOnHidden
        >
            <Tabs
                defaultActiveKey="basic"
                items={[
                    {
                        key: 'basic',
                        label: (
                            <span>
                                <EyeOutlined />
                                基本信息
                            </span>
                        ),
                        children: (
                            <Descriptions bordered column={2} loading={loadingTemplate ? true : false} className="template-descriptions">
                        <Descriptions.Item label="模板代码" span={1}>
                            <Text code>{displayTemplate.template_code}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="模板名称" span={1}>
                            {displayTemplate.name}
                        </Descriptions.Item>

                        <Descriptions.Item label="分类" span={1}>
                            {getCategoryText(displayTemplate.category)}
                        </Descriptions.Item>
                        <Descriptions.Item label="状态" span={1}>
                            {getStatusTag(displayTemplate.status)}
                        </Descriptions.Item>

                        <Descriptions.Item label="类型" span={1}>
                            <Tag color={displayTemplate.is_system ? "blue" : "default"}>{displayTemplate.is_system ? "系统模板" : "自定义模板"}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="可见性" span={1}>
                            <Tag color={displayTemplate.is_public ? "green" : "orange"}>{displayTemplate.is_public ? "公共模板" : "私有模板"}</Tag>
                        </Descriptions.Item>

                        <Descriptions.Item label="使用次数" span={1}>
                            {displayTemplate.usage_count}
                        </Descriptions.Item>
                        <Descriptions.Item label="最后使用时间" span={1}>
                            {displayTemplate.last_used_at ? formatDateTime(displayTemplate.last_used_at) : "未使用"}
                        </Descriptions.Item>

                        <Descriptions.Item label="创建时间" span={1}>
                            {formatDateTime(displayTemplate.created_at)}
                        </Descriptions.Item>
                        <Descriptions.Item label="更新时间" span={1}>
                            {formatDateTime(displayTemplate.updated_at)}
                        </Descriptions.Item>

                        <Descriptions.Item label="描述" span={2}>
                            {displayTemplate.description || "无描述"}
                        </Descriptions.Item>

                        <Descriptions.Item label="邮件主题模板" span={2}>
                            <Text code>{displayTemplate.subject_template}</Text>
                        </Descriptions.Item>

                        {/* 适配机场 */}
                        {displayTemplate.applicable_airports && displayTemplate.applicable_airports.length > 0 && (
                            <Descriptions.Item label="适配机场" span={2}>
                                <div className="tag-group">
                                    {displayTemplate.applicable_airports.map((airport) => (
                                        <Tag key={airport} color="blue">
                                            {airport}
                                        </Tag>
                                    ))}
                                </div>
                            </Descriptions.Item>
                        )}

                        {/* 适配供应商 */}
                        {displayTemplate.applicable_suppliers && displayTemplate.applicable_suppliers.length > 0 && (
                            <Descriptions.Item label="适配供应商" span={2}>
                                <div className="tag-group">
                                    {displayTemplate.applicable_suppliers.map((supplierId) => (
                                        <Tag key={supplierId} color="green">
                                            供应商ID: {supplierId}
                                        </Tag>
                                    ))}
                                </div>
                            </Descriptions.Item>
                        )}

                        {displayTemplate.tags && displayTemplate.tags.length > 0 && (
                            <Descriptions.Item label="标签" span={2}>
                                <div className="tag-group">
                                    {displayTemplate.tags.map((tag) => (
                                        <Tag key={tag}>{tag}</Tag>
                                    ))}
                                </div>
                            </Descriptions.Item>
                        )}

                        {displayTemplate.content_blocks && displayTemplate.content_blocks.length > 0 && (
                            <Descriptions.Item label="内容块" span={2}>
                                <div className="tag-group">
                                    {displayTemplate.content_blocks.map((block) => (
                                        <Tag key={block} color="blue">
                                            {block}
                                        </Tag>
                                    ))}
                                </div>
                            </Descriptions.Item>
                        )}

                        {/* 收件人配置信息 */}
                        {(displayTemplate.default_to || displayTemplate.default_cc || displayTemplate.default_bcc) && (
                            <Descriptions.Item label="收件人配置" span={2}>
                                <Card size="small" style={{ backgroundColor: "#fafafa" }}>
                                    <Space direction="vertical" style={{ width: "100%" }} size="middle">
                                        <RecipientConfigDisplay config={displayTemplate.default_to} title="默认收件人" icon={<ContactsOutlined style={{ color: "#1890ff" }} />} />
                                        <RecipientConfigDisplay config={displayTemplate.default_cc} title="默认抄送人" icon={<ContactsOutlined style={{ color: "#52c41a" }} />} />
                                        <RecipientConfigDisplay config={displayTemplate.default_bcc} title="默认密送人" icon={<ContactsOutlined style={{ color: "#faad14" }} />} />
                                    </Space>
                                </Card>
                            </Descriptions.Item>
                        )}

                        {/* 如果没有配置收件人，显示提示信息 */}
                        {!displayTemplate.default_to && !displayTemplate.default_cc && !displayTemplate.default_bcc && (
                            <Descriptions.Item label="收件人配置" span={2}>
                                <Alert message="该模板未配置默认收件人" description="使用此模板时需要手动指定收件人、抄送人和密送人。" type="info" showIcon style={{ backgroundColor: "#f6ffed", border: "1px solid #b7eb8f" }} />
                            </Descriptions.Item>
                        )}
                    </Descriptions>
                        )
                    },
                    {
                        key: 'recipient-editor',
                        label: (
                            <span>
                                <EditOutlined />
                                收件人编辑
                            </span>
                        ),
                        children: <RecipientEditor templateData={displayTemplate} onSave={handleSaveRecipientConfig} loading={loadingTemplate} />
                    },
                    {
                        key: 'scope-editor',
                        label: (
                            <span>
                                <GlobalOutlined />
                                适用范围
                            </span>
                        ),
                        children: <ScopeEditor templateData={displayTemplate} onSave={handleSaveScopeConfig} loading={loadingTemplate} />
                    },
                    {
                        key: 'preview',
                        label: (
                            <span>
                                <EyeOutlined />
                                邮件预览
                            </span>
                        ),
                        children: <EmailPreview subject={displayTemplate.subject_template} bodyHtml={displayTemplate.body_html} loading={loadingTemplate} templateData={displayTemplate} />
                    },

                    {
                        key: 'source',
                        label: (
                            <span>
                                <CodeOutlined />
                                源代码
                            </span>
                        ),
                        children: (
                            loadingTemplate ? (
                                <div style={{ textAlign: "center", padding: "40px 0" }}>
                                    <Text type="secondary">正在加载模板内容...</Text>
                                </div>
                            ) : (
                                <div className="source-code-container">
                                    <Card title="HTML 源代码" size="small">
                                        <div
                                            style={{
                                                maxHeight: "400px",
                                                overflow: "auto",
                                                border: "1px solid #d9d9d9",
                                                padding: "12px",
                                                backgroundColor: "#fafafa",
                                                borderRadius: "4px",
                                            }}
                                        >
                                            <Paragraph>
                                                <pre style={{ whiteSpace: "pre-wrap", fontSize: "12px", margin: 0, fontFamily: "Monaco, Consolas, monospace" }}>{displayTemplate.body_html || "暂无HTML内容"}</pre>
                                            </Paragraph>
                                        </div>
                                    </Card>

                                    {displayTemplate.body_text && (
                                        <Card title="纯文本内容" size="small">
                                            <div
                                                style={{
                                                    maxHeight: "300px",
                                                    overflow: "auto",
                                                    border: "1px solid #d9d9d9",
                                                    padding: "12px",
                                                    backgroundColor: "#fafafa",
                                                    borderRadius: "4px",
                                                }}
                                            >
                                                <Paragraph>
                                                    <pre style={{ whiteSpace: "pre-wrap", margin: 0, fontFamily: "Monaco, Consolas, monospace" }}>{displayTemplate.body_text}</pre>
                                                </Paragraph>
                                            </div>
                                        </Card>
                                    )}
                                </div>
                            )
                        )
                    },

                    {
                        key: 'versions',
                        label: (
                            <span>
                                <HistoryOutlined />
                                版本历史
                            </span>
                        ),
                        children: (
                            <>
                                <Timeline
                                    loading={loadingVersions}
                                    className="version-timeline"
                                    items={versions.map((version) => ({
                                        dot: <ClockCircleOutlined />,
                                        children: (
                                            <div>
                                                <div style={{ marginBottom: 8 }}>
                                                    <Text strong>版本 {version.version_number}</Text>
                                                    <Text type="secondary" style={{ marginLeft: 16 }}>
                                                        {formatDateTime(version.created_at)}
                                                    </Text>
                                                </div>
                                                {version.change_summary && (
                                                    <div style={{ marginBottom: 8 }}>
                                                        <Text>{version.change_summary}</Text>
                                                    </div>
                                                )}
                                                <div>
                                                    <Text type="secondary">
                                                        <UserOutlined style={{ marginRight: 4 }} />
                                                        用户ID: {version.created_by || "系统"}
                                                    </Text>
                                                </div>
                                            </div>
                                        ),
                                    }))}
                                />

                                {versions.length === 0 && !loadingVersions && (
                                    <div style={{ textAlign: "center", padding: "40px 0" }}>
                                        <Text type="secondary">暂无版本历史</Text>
                                    </div>
                                )}
                            </>
                        )
                    }
                ]}
            />
        </Modal>
    );
};

export default EmailTemplateDetail;
